import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { stockAPI } from "../../lib/api";
import { ArrowLeft, BookOpen, PackagePlus, PackageMinus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const fmtBRL = v => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const fmtDate = iso => iso ? new Date(iso).toLocaleDateString("pt-BR", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";

const TYPE_META = {
    entrada: { label: "Entrada", color: "text-green-400", dot: "bg-green-500", sign: "+" },
    saida:   { label: "Saída",   color: "text-red-400",   dot: "bg-red-500",   sign: "-" },
    ajuste:  { label: "Ajuste",  color: "text-blue-400",  dot: "bg-blue-500",  sign: "~" },
};

export default function StockProductDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [movements, setMovements] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        stockAPI.getProduct(id)
            .then(r => { setProduct(r.data); setMovements(r.data.recent_movements || []); })
            .catch(() => { toast.error("Produto não encontrado"); navigate("/estoque/produtos"); })
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return <div className="p-6 text-stone-400 text-sm">Carregando...</div>;
    if (!product) return null;

    const low = product.stock_current <= product.stock_min;
    const margin = product.price && product.cost ? ((product.price - product.cost) / product.price * 100).toFixed(1) : null;

    return (
        <div className="p-6 space-y-6 max-w-3xl">
            <div className="flex items-center gap-3">
                <button onClick={() => navigate("/estoque/produtos")}
                    className="p-2 rounded-lg text-stone-400 hover:text-stone-100 hover:bg-stone-800 transition-colors">
                    <ArrowLeft className="w-4 h-4" />
                </button>
                <div className={`p-3 rounded-xl ${low ? "bg-red-900/30" : "bg-amber-900/20"}`}>
                    <BookOpen className={`w-6 h-6 ${low ? "text-red-400" : "text-amber-400"}`} />
                </div>
                <div>
                    <h1 className="text-xl font-bold text-stone-100">{product.name}</h1>
                    <p className="text-stone-400 text-sm">{product.isbn && `ISBN ${product.isbn}`} {product.edition && `· ${product.edition}`}</p>
                </div>
            </div>

            {low && (
                <div className="bg-orange-950/30 border border-orange-800/50 rounded-xl p-4 flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0" />
                    <div className="flex-1">
                        <p className="text-sm font-medium text-orange-200">Estoque abaixo do mínimo</p>
                        <p className="text-xs text-orange-400/70">Atual: {product.stock_current} · Mínimo: {product.stock_min}</p>
                    </div>
                    <Link to="/estoque/entrada" className="text-xs text-orange-300 hover:text-orange-100 border border-orange-700 rounded-lg px-3 py-1.5 transition-colors">
                        Repor →
                    </Link>
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    { label: "Estoque atual", value: product.stock_current, color: low ? "text-red-400" : "text-stone-100" },
                    { label: "Mínimo", value: product.stock_min },
                    { label: "Preço venda", value: product.price ? fmtBRL(product.price) : "—" },
                    { label: "Margem", value: margin ? `${margin}%` : "—" },
                ].map(stat => (
                    <div key={stat.label} className="bg-stone-900 border border-stone-800 rounded-xl p-4">
                        <p className="text-xs text-stone-500 mb-1">{stat.label}</p>
                        <p className={`text-xl font-bold ${stat.color || "text-stone-100"}`}>{stat.value}</p>
                    </div>
                ))}
            </div>

            <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-semibold text-stone-200">Histórico de movimentações</h2>
                    <Link to="/estoque/entrada"
                        className="flex items-center gap-1.5 text-xs text-amber-500 hover:text-amber-400 border border-amber-700/50 rounded-lg px-3 py-1.5 transition-colors">
                        <PackagePlus className="w-3.5 h-3.5" /> Nova movimentação
                    </Link>
                </div>
                {movements.length === 0 ? (
                    <p className="text-xs text-stone-500 text-center py-6">Nenhuma movimentação registrada.</p>
                ) : (
                    <div className="space-y-2">
                        {movements.map(m => {
                            const meta = TYPE_META[m.type] || TYPE_META.ajuste;
                            return (
                                <div key={m.id} className="flex items-center gap-3 py-2.5 border-b border-stone-800/50 last:border-0">
                                    <div className={`w-2 h-2 rounded-full ${meta.dot} flex-shrink-0`} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className={`text-xs font-medium ${meta.color}`}>{meta.label}</span>
                                            {m.reason && <span className="text-xs text-stone-500">{m.reason}</span>}
                                        </div>
                                        {(m.buyer_name || m.buyer_address) && (
                                            <p className="text-xs text-stone-500 mt-0.5">
                                                {m.buyer_name}{m.buyer_address && ` · ${m.buyer_address}`}
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <p className={`text-sm font-bold ${meta.color}`}>{meta.sign}{m.quantity}</p>
                                        <p className="text-xs text-stone-600">{fmtDate(m.created_at)}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
